import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { formatImperial, formatMetric } from '../measure-overlay/measure-utils';
import { ParsedKnownLength, parseKnownLength } from '../measure-overlay/known-length';
import {
  AutomaticScale,
  CalibrationPreview,
  NormalizedLine,
  PageScaleHistoryEntry,
  PageScaleState,
  PageSizePoints,
  ScaleWarning,
  describeScaleFactor,
  formatSignedPercent,
  previewCalibration,
} from '../measure-overlay/scale-calibration';

/** What the reviewer confirmed when pressing Apply calibration. */
export interface ApplyCalibrationEvent {
  /** The real length exactly as typed. */
  enteredValue: string;
  note: string;
  /** True when warnings were shown and the reviewer ticked the confirmation. */
  acknowledgeWarnings: boolean;
}

/** One line of the confirmation summary or the saved-calibration details. */
interface SummaryRow {
  label: string;
  value: string;
  testId: string;
}

/**
 * The panel beside the Preview for calibrating a page's scale and inspecting a saved calibration
 * (Issue #2442).
 *
 * It has two views. *Calibrate* walks the reviewer through the task: mark both ends of a known
 * dimension, type its real length, check the summary, apply. *Details* shows the saved
 * calibration, who made it, the detected scale it replaced, and the actions that change it.
 *
 * The panel does not talk to the server and does not own the line: the Preview passes in where the
 * line is and what the page's scale state is, and acts on the events emitted here. What the panel
 * does own is the typed length, which it parses on every keystroke so that an unusable value is
 * explained beside the field before anything is sent.
 */
@Component({
  selector: 'app-scale-calibration-panel',
  imports: [
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
  ],
  templateUrl: './scale-calibration-panel.component.html',
  styleUrls: ['./scale-calibration-panel.component.scss'],
})
export class ScaleCalibrationPanelComponent implements OnChanges, AfterViewInit {
  /** Which view to show. */
  @Input() view: 'calibrate' | 'details' = 'calibrate';
  /** The traced line in unrotated page space; null until both ends are placed. */
  @Input() line: NormalizedLine | null = null;
  /** The unrotated page size in PDF points; null until the page is rendered. */
  @Input() pageSize: PageSizePoints | null = null;
  @Input() correctionFactor = 1;
  /** The scale detected in the page transcript. */
  @Input() automatic: AutomaticScale = { scaleText: null, scaleFactor: null };
  /** The page's saved scale state. */
  @Input() state: PageScaleState | null = null;
  /** True while a save or reset is in flight. */
  @Input() busy = false;
  /** An explanation from the server: a conflict, a changed page, or a refusal. */
  @Input() serverMessage: string | null = null;
  /** Warnings the server asked the reviewer to confirm. */
  @Input() serverWarnings: ScaleWarning[] = [];
  /** True while the detected scale is temporarily shown instead of the manual one. */
  @Input() comparing = false;
  /** The page's calibration history; null while it has not been loaded. */
  @Input() history: PageScaleHistoryEntry[] | null = null;
  /**
   * Which side of the Preview the panel is docked on. The drawing area gives up that side while
   * the panel is open, so the panel never covers the drawing; the reviewer can move it to the
   * other side with the button in its header.
   */
  @Input() placement: 'left' | 'right' = 'left';

  /** The reviewer pressed Apply calibration with a line and a valid length. */
  @Output() apply = new EventEmitter<ApplyCalibrationEvent>();
  /** The reviewer left calibration without saving. */
  @Output() cancelCalibration = new EventEmitter<void>();
  /** The reviewer asked for a line to be placed for them, to adjust with the keyboard. */
  @Output() placeLine = new EventEmitter<void>();
  /** The reviewer asked to trace the line again. */
  @Output() redrawLine = new EventEmitter<void>();
  @Output() recalibrate = new EventEmitter<void>();
  @Output() resetToAutomatic = new EventEmitter<void>();
  @Output() toggleCompare = new EventEmitter<void>();
  @Output() closeDetails = new EventEmitter<void>();
  /** The reviewer asked for the panel to move to the other side of the Preview. */
  @Output() movePanel = new EventEmitter<void>();

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild('lengthInput') lengthInput?: ElementRef<HTMLInputElement>;
  @ViewChild('heading') heading?: ElementRef<HTMLElement>;

  /** The length as typed. */
  enteredValue = '';
  note = '';
  /** Whether the reviewer ticked "I have checked the line and the length". */
  warningsAcknowledged = false;
  /** False until the reviewer has typed something, so an empty field is not shown as an error. */
  lengthTouched = false;

  /** The typed length, understood; null when it is empty or unusable. */
  parsed: ParsedKnownLength | null = null;
  /** Why the typed length cannot be used; null when it can, or nothing was typed yet. */
  lengthError: string | null = null;
  /** What the line and the length would produce; null until there is a line. */
  preview: CalibrationPreview | null = null;

  /**
   * Moves keyboard focus into the panel when it opens or changes view. Without this, focus stays
   * on the toolbar button, the next Tab goes to the rest of the toolbar, and the first Escape is
   * spent closing that button's tooltip instead of cancelling.
   */
  private focusHeading(): void {
    setTimeout(() => {
      // A reviewer who is already moving an end of the line with the keyboard keeps their place.
      const focused = document.activeElement?.getAttribute('data-testid') ?? '';
      if (focused.startsWith('calibration-handle')) return;
      // Opened from a finished ruler line (Use as scale reference, Issue #2542): the line is
      // already there, so the next step is typing its known length.
      if (this.view === 'calibrate' && this.line && this.lengthInput) {
        this.lengthInput.nativeElement.focus({ preventScroll: true });
        return;
      }
      this.heading?.nativeElement.focus({ preventScroll: true });
    });
  }

  ngAfterViewInit(): void {
    this.focusHeading();
  }

  /**
   * Escape closes the details view from wherever focus is, and cancels a calibration when focus is
   * in one of this form's text fields (Issue #2542). The form opens with the cursor in Known
   * length, and the measurement overlay, which handles Escape everywhere else during a
   * calibration, deliberately leaves keys typed into text fields alone. Without this, the first
   * Escape a keyboard user presses would do nothing although the form says "Press Esc to cancel".
   * A menu that is open takes the key first. A menu that has been dismissed but is still animating
   * away does not: its trigger no longer reports it as expanded, and the reviewer's Escape is meant
   * for this panel.
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (event.defaultPrevented) return;
    if (
      document.querySelector(
        '[aria-haspopup="menu"][aria-expanded="true"], [aria-haspopup="dialog"][aria-expanded="true"]'
      )
    ) {
      return;
    }
    if (this.view === 'details') {
      this.closeDetails.emit();
      return;
    }
    const target = event.target;
    const inOwnTextField =
      (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
      this.host.nativeElement.contains(target);
    if (this.view === 'calibrate' && inOwnTextField) {
      event.preventDefault();
      this.cancelCalibration.emit();
    }
  }

  /** The scale a history entry resulted in, such as 1:48 (1/4" = 1'-0"). */
  historyScale(entry: PageScaleHistoryEntry): string {
    return entry.calibration ? describeScaleFactor(entry.calibration.drawingScaleFactor) : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['view'] && !changes['view'].firstChange) this.focusHeading();
    if (changes['view'] && this.view === 'calibrate') {
      this.enteredValue = '';
      this.note = '';
      this.lengthTouched = false;
      this.warningsAcknowledged = false;
    }
    if (changes['line']) {
      // A different line is a different calibration; an earlier confirmation does not carry over.
      this.warningsAcknowledged = false;
      const placedNow = !!this.line && !changes['line'].previousValue;
      if (placedNow && this.view === 'calibrate') {
        // Both ends are placed: the next thing to do is type the length. A reviewer who is moving
        // the ends with the keyboard keeps focus on the handle they are using.
        setTimeout(() => {
          const focused = document.activeElement?.getAttribute('data-testid') ?? '';
          if (!focused.startsWith('calibration-handle')) {
            this.lengthInput?.nativeElement.focus();
          }
        });
      }
    }
    this.recompute();
  }

  onLengthInput(value: string): void {
    this.enteredValue = value;
    this.lengthTouched = true;
    this.warningsAcknowledged = false;
    this.recompute();
  }

  /** Every warning to confirm: the ones computed here plus any the server added. */
  get warnings(): ScaleWarning[] {
    const local = this.preview?.derived?.warnings ?? [];
    const extra = this.serverWarnings.filter(warning => !local.some(known => known.code === warning.code));
    return [...local, ...extra];
  }

  /** True when Apply calibration can be pressed. */
  get canApply(): boolean {
    return (
      !this.busy &&
      !!this.line &&
      !!this.parsed &&
      !!this.preview?.derived &&
      (this.warnings.length === 0 || this.warningsAcknowledged)
    );
  }

  /** The typed length in its standard form with its equivalent, e.g. `11'-0" (3.35 m)`. */
  get normalizedLength(): string | null {
    if (!this.parsed) return null;
    const other = this.parsed.metric ? formatImperial(this.parsed.inches) : formatMetric(this.parsed.inches);
    return `${this.parsed.normalized} (${other})`;
  }

  /** Rows of the confirmation summary shown before applying. */
  get summaryRows(): SummaryRow[] {
    const derived = this.preview?.derived;
    if (!derived || !this.parsed) return [];
    const rows: SummaryRow[] = [
      { label: 'Reference length', value: this.parsed.normalized, testId: 'calibration-summary-reference' },
      { label: 'Derived scale', value: derived.scaleLabel, testId: 'calibration-summary-derived' },
      { label: 'Detected scale', value: this.detectedScaleText, testId: 'calibration-summary-detected' },
    ];
    if (derived.differencePercent !== null) {
      rows.push({
        label: 'Difference',
        value: formatSignedPercent(derived.differencePercent),
        testId: 'calibration-summary-difference',
      });
    }
    return rows;
  }

  /** Rows describing the saved calibration in the details view. */
  get detailRows(): SummaryRow[] {
    const calibration = this.state?.calibration;
    if (!calibration) return [];
    const rows: SummaryRow[] = [
      {
        label: 'Reference length',
        value:
          calibration.normalizedValue === calibration.enteredValue
            ? calibration.normalizedValue
            : `${calibration.normalizedValue} (entered as ${calibration.enteredValue})`,
        testId: 'calibration-detail-reference',
      },
      {
        label: 'Manual scale',
        value: describeScaleFactor(calibration.drawingScaleFactor),
        testId: 'calibration-detail-scale',
      },
      {
        label: 'Detected scale',
        value: calibration.automaticCandidate
          ? calibration.automaticCandidate.scaleText ||
            describeScaleFactor(calibration.automaticCandidate.drawingScaleFactor)
          : 'None detected when calibrated',
        testId: 'calibration-detail-detected',
      },
    ];
    if (calibration.differenceFromAutomaticPercent !== null) {
      rows.push({
        label: 'Difference',
        value: formatSignedPercent(calibration.differenceFromAutomaticPercent),
        testId: 'calibration-detail-difference',
      });
    }
    if (calibration.note) {
      rows.push({ label: 'Note', value: calibration.note, testId: 'calibration-detail-note' });
    }
    return rows;
  }

  get detectedScaleText(): string {
    if (this.automatic.scaleFactor === null) return 'None detected';
    return this.automatic.scaleText ?? describeScaleFactor(this.automatic.scaleFactor);
  }

  /** Length of the traced line on the sheet, for the first step's confirmation. */
  get sheetLengthText(): string | null {
    return this.preview ? `${this.preview.sheetInches.toFixed(2)} in on the sheet` : null;
  }

  onSubmit(): void {
    this.lengthTouched = true;
    this.recompute();
    if (!this.canApply) return;
    this.apply.emit({
      enteredValue: this.enteredValue.trim(),
      note: this.note.trim(),
      acknowledgeWarnings: this.warnings.length > 0,
    });
  }

  private recompute(): void {
    const result = parseKnownLength(this.enteredValue);
    this.parsed = result.ok ? result : null;
    this.lengthError = !result.ok && this.lengthTouched ? result.message : null;
    this.preview =
      this.line && this.pageSize
        ? previewCalibration(
            this.line,
            this.pageSize,
            this.parsed?.inches ?? null,
            this.correctionFactor,
            this.automatic
          )
        : null;
  }
}
